import { useMemo, useState } from "react";
import { FolderInput, HardDrive, Loader2, Search } from "lucide-react";
import type { DriveItem, FolderRef } from "@/api/driveApi";
import { useAllFolders } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CATEGORY_STYLE } from "@/lib/fileTypes";
import { cn } from "@/lib/utils";

/**
 * "Move to…" folder picker. Excludes the moving folders and everything
 * inside them (the server also guards cycles). Click to choose, then
 * "Move here"; double-click moves immediately.
 */
export function MoveDialog({
    items,
    onClose,
    onPick,
    mode = "move"
}: {
    items: DriveItem[];
    onClose: () => void;
    onPick: (targetId: string | null) => void;
    /** "copy" = "Copy to…": the current folder is a valid target too. */
    mode?: "move" | "copy";
}) {
    const copying = mode === "copy";
    const folders = useAllFolders();
    const [filter, setFilter] = useState("");
    const [target, setTarget] = useState<string | null | undefined>(undefined);

    const byId = useMemo(() => new Map((folders.data ?? []).map((f) => [f.id, f])), [folders.data]);

    const excluded = useMemo(() => {
        const moving = new Set(items.filter((i) => i.kind === "Folder").map((i) => i.id));
        const out = new Set<string>();
        for (const f of folders.data ?? []) {
            let cur: FolderRef | undefined = f;
            for (let depth = 0; cur && depth < 64; depth++) {
                if (moving.has(cur.id)) {
                    out.add(f.id);
                    break;
                }
                cur = cur.parentId ? byId.get(cur.parentId) : undefined;
            }
        }
        return out;
    }, [items, folders.data, byId]);

    const parents = new Set(items.map((i) => i.parentId ?? "ROOT"));
    const sameParent = parents.size === 1 ? [...parents][0] : undefined;
    const isCurrent = (id: string | null) => !copying && sameParent === (id ?? "ROOT");

    const rows = useMemo(() => {
        const pathOf = (f: FolderRef): string => {
            const segs = [f.name];
            let pid = f.parentId;
            for (let i = 0; i < 32 && pid; i++) {
                const parent = byId.get(pid);
                if (!parent) break;
                segs.unshift(parent.name);
                pid = parent.parentId;
            }
            return segs.join(" / ");
        };
        const q = filter.trim().toLowerCase();
        return (folders.data ?? [])
            .filter((f) => !excluded.has(f.id))
            .map((f) => ({ f, path: pathOf(f) }))
            .filter((r) => !q || r.path.toLowerCase().includes(q))
            .sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true, sensitivity: "base" }));
    }, [folders.data, excluded, filter, byId]);

    const verb = copying ? "Copy" : "Move";
    const title = items.length === 1 ? `${verb} “${items[0]!.name}”${copying ? " to…" : ""}` : `${verb} ${items.length} items${copying ? " to…" : ""}`;
    const folderStyle = CATEGORY_STYLE.folder;

    const row = (id: string | null, name: string, sub: string | null, icon: React.ReactNode) => {
        const current = isCurrent(id);
        const selected = target === id;
        return (
            <button
                key={id ?? "root"}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={current}
                onClick={() => setTarget(id)}
                onDoubleClick={() => !current && onPick(id)}
                className={cn(
                    "flex w-full items-center gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default disabled:opacity-50",
                    selected ? "bg-primary/10 dark:bg-primary/20" : "hover:bg-accent"
                )}
            >
                {icon}
                <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{name}</div>
                    {sub ? <div className="truncate text-xs text-muted-foreground">{sub}</div> : null}
                </div>
                {current ? <span className="shrink-0 text-xs text-muted-foreground">Current location</span> : null}
            </button>
        );
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title={title}
            icon={<FolderInput className="h-5 w-5 text-primary" />}
            size="md"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => target !== undefined && onPick(target)} disabled={target === undefined || isCurrent(target)}>
                        {verb} here
                    </Button>
                </>
            }
        >
            <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                    type="search"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Find a folder"
                    aria-label="Find a folder"
                    className="pl-8"
                    data-autofocus=""
                />
            </div>
            <div role="listbox" aria-label="Destination folders" className="max-h-[45vh] overflow-y-auto rounded-lg border">
                {!filter ? row(null, "My Drive", null, <HardDrive className="h-5 w-5 shrink-0 text-primary" aria-hidden />) : null}
                {folders.isPending ? (
                    <p className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading folders…
                    </p>
                ) : rows.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-muted-foreground">{filter ? `No folders match “${filter}”.` : "No other folders yet."}</p>
                ) : (
                    rows.map(({ f, path }) =>
                        row(f.id, f.name, path, <folderStyle.icon className={cn("h-5 w-5 shrink-0", folderStyle.color)} aria-hidden />)
                    )
                )}
            </div>
        </Dialog>
    );
}
